/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AgentProvider, useAgentContext } from './contexts/AgentContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import PwaInstallBanner from './components/PwaInstallBanner';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Voice from './pages/Voice';
import Chat from './pages/Chat';
import Preferences from './pages/Preferences';
import Subscription from './pages/Subscription';
import { Customers } from './pages/Customers';
import { History } from './pages/History';

function AppRoutes() {
  const { isAuthenticated, loading } = useAgentContext();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <PwaInstallBanner />
      <Routes>
        <Route path="/" element={!isAuthenticated ? <Landing /> : <Navigate to="/dashboard" />} />
        <Route
          path="/login"
          element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" />}
        />
        <Route
          path="/dashboard"
          element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />}
        />
        <Route
          path="/preferences"
          element={isAuthenticated ? <Preferences /> : <Navigate to="/login" />}
        />
        <Route
          path="/subscription"
          element={isAuthenticated ? <Subscription /> : <Navigate to="/login" />}
        />
        <Route path="/chat" element={isAuthenticated ? <Chat /> : <Navigate to="/login" />} />
        <Route path="/voice" element={isAuthenticated ? <Voice /> : <Navigate to="/login" />} />
        <Route path="/customers" element={isAuthenticated ? <Customers /> : <Navigate to="/login" />} />
        {/** @deprecated Legacy flat history view — prefer /customers; remove in a future release. */}
        <Route path="/history" element={isAuthenticated ? <History /> : <Navigate to="/login" />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AgentProvider>
          <div className="min-h-screen bg-background">
            <AppRoutes />
          </div>
        </AgentProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
